import { Sun, Moon, HelpCircle, Info, Shield, Settings, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Text } from '../../base/Text';
import type { DrawerMenuItem } from '../../../hooks/useDrawerMenuItems';
import styles from './DrawerLayout.module.css';

const ICON_MAP: Record<string, typeof Sun> = {
  sun: Sun,
  moon: Moon,
  'help-circle': HelpCircle,
  info: Info,
  shield: Shield,
  settings: Settings,
  'log-out': LogOut,
};

interface DrawerContentProps {
  items: DrawerMenuItem[];
  buildVersion?: string;
  onClose: () => void;
}

export const DrawerContent = ({ items, buildVersion, onClose }: DrawerContentProps) => {
  const { t } = useTranslation();

  const handleItemPress = (item: DrawerMenuItem) => {
    onClose();
    setTimeout(() => item.onPress(), 220);
  };

  return (
    <div className={styles.drawer}>
      <div className={styles.header}>
        <Text variant="title">{t('drawer.menu', 'Menu')}</Text>
        <button type="button" onClick={onClose} className={styles.closeBtn} aria-label="Close">
          ✕
        </button>
      </div>
      <nav className={styles.content}>
        {items.map((item) => {
          const Icon = ICON_MAP[item.icon] ?? Info;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleItemPress(item)}
              className={item.destructive ? styles.itemDestructive : styles.item}
            >
              <Icon size={20} />
              <span>{item.label}</span>
              <span className={styles.chevron}>›</span>
            </button>
          );
        })}
      </nav>
      {buildVersion && (
        <div className={styles.footer}>
          <Text variant="meta" muted>
            {buildVersion}
          </Text>
        </div>
      )}
    </div>
  );
};

interface DrawerLayoutProps {
  isOpen: boolean;
  onClose: () => void;
  drawerContent: React.ReactNode;
  children: React.ReactNode;
}

export const DrawerLayout = ({ isOpen, onClose, drawerContent, children }: DrawerLayoutProps) => {
  return (
    <div className={styles.wrapper}>
      {children}
      {isOpen && (
        <>
          <div className={styles.overlay} onClick={onClose} aria-hidden="true" />
          <aside className={styles.panel} role="dialog">
            {drawerContent}
          </aside>
        </>
      )}
    </div>
  );
};
